/**
 * Broker Data Pipeline → Overview.
 *
 * Every writer in the core's closed list, its window's work, and its budget's use over the budget's own period (ADR-047 §3.3),
 * and its failed runs. No alert pages anyone over budget: a runaway writer is read here. A writer with no code yet is
 * listed as such, never as a quiet writer with zero counts.
 */

import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import Link from '@mui/joy/Link'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { BrokerPipelineOverviewWriter } from '@numeo/types-core'
import * as React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { brokerPipelineApi } from 'api/brokerPipeline'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, formatNumber, useLoadPipelineData } from '../load-pipeline/components/shared'
import { budgetLabel, budgetPeriod, budgetShare, quietFor, writerPath, writerTitle } from './components/shared'

const count = (w: BrokerPipelineOverviewWriter, counter: string): number => w.totals?.counters[counter] ?? 0

function Status({ writer }: { writer: BrokerPipelineOverviewWriter }) {
    if (!writer.built)
        return (
            <Chip size="sm" variant="soft">
                not yet built
            </Chip>
        )
    if (writer.unavailable)
        return (
            <Chip size="sm" color="warning" variant="soft">
                counters unreadable
            </Chip>
        )
    return (
        <Chip size="sm" color="success" variant="soft">
            built
        </Chip>
    )
}

/** Published over the budget's own period against the budget; over it reads as a warning, never as an error. */
function Budget({ writer }: { writer: BrokerPipelineOverviewWriter }) {
    if (writer.budget === null) return <Typography level="body-xs">none declared</Typography>
    const share = budgetShare(writer.budget, writer.budgetUse)
    if (share === null) return <Typography level="body-xs">{budgetLabel(writer.budget)}</Typography>
    return (
        <Typography
            level="body-xs"
            color={share > 1 ? 'warning' : undefined}
            fontWeight={share > 1 ? 'lg' : undefined}
            title={`published over ${budgetPeriod(writer.budget)}`}
        >
            {budgetLabel(writer.budget)} ({Math.round(share * 100)}% used)
        </Typography>
    )
}

export default function BrokerPipelineOverviewPage() {
    const [hours, setHours] = React.useState(24)
    const { data, error, loading } = useLoadPipelineData(() => brokerPipelineApi.getOverview(hours), [hours])

    const built = data?.writers.filter((w) => w.built) ?? []
    const published = built.reduce((sum, w) => sum + count(w, 'published'), 0)
    const failedRuns = built.reduce((sum, w) => sum + count(w, 'runsFailed'), 0)
    const quarantined = built.reduce((sum, w) => sum + count(w, 'quarantined'), 0)

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography level="h2">Broker Data Pipeline</Typography>
                <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 3 }}>
                Each writer counts what it published, refused, found unchanged and quarantined, per hour, in its own collection (ADR-047 §3.3). Its ADR declares
                a budget per day, or per week for a weekly writer; its use is read over that period, whatever the window.
            </Typography>

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}

            {data ? (
                <>
                    <StatRow>
                        <Stat label="Writers built" value={`${built.length} of ${data.writers.length}`} hint="the others are declared in the charter only" />
                        <Stat label="Published" value={formatNumber(published)} hint="updates every built writer put on brokerpipe-updates" />
                        <Stat
                            label="Quarantined"
                            value={formatNumber(quarantined)}
                            hint="observations refused with a note"
                            tone={quarantined > 0 ? 'warning' : 'neutral'}
                        />
                        <Stat
                            label="Failed runs"
                            value={formatNumber(failedRuns)}
                            hint="a scheduled run or seed that stopped; its trigger already answered 202"
                            tone={failedRuns > 0 ? 'danger' : 'neutral'}
                        />
                    </StatRow>

                    {built.length > 0 && published === 0 && built.every((w) => !w.totals?.lastEventAt) ? (
                        <Alert color="neutral" variant="soft" sx={{ mb: 3 }}>
                            No writer did anything in this window. Before the FMCSA seed runs this is expected: nothing has been published yet.
                        </Alert>
                    ) : null}

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                        <Table size="sm" stripe="odd">
                            <thead>
                                <tr>
                                    <th style={{ width: 140 }}>Writer</th>
                                    <th style={{ width: 120 }}>Status</th>
                                    <th style={{ width: 110 }}>Type</th>
                                    <th style={{ textAlign: 'right' }}>Published</th>
                                    <th style={{ width: 170, textAlign: 'right' }}>Budget</th>
                                    <th style={{ textAlign: 'right' }}>Unchanged</th>
                                    <th style={{ textAlign: 'right' }}>Refused</th>
                                    <th style={{ textAlign: 'right' }}>Quarantined</th>
                                    <th style={{ textAlign: 'right' }}>Runs ok</th>
                                    <th style={{ textAlign: 'right' }}>Runs failed</th>
                                    <th style={{ width: 150, textAlign: 'right' }}>Last event</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.writers.map((w) => (
                                    <tr key={w.writer}>
                                        <td>
                                            <Link component={RouterLink} to={writerPath(w.writer)} level="body-sm">
                                                {writerTitle(w.writer)}
                                            </Link>
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {w.adr ?? 'no ADR yet'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Status writer={w} />
                                        </td>
                                        <td>
                                            <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                                {w.types.join(' · ')}
                                            </Typography>
                                        </td>
                                        {w.built && w.totals ? (
                                            <>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(count(w, 'published'))}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <Budget writer={w} />
                                                </td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(count(w, 'unchanged'))}</td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(count(w, 'refused'))}</td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(count(w, 'quarantined'))}</td>
                                                <td style={{ textAlign: 'right' }}>{formatNumber(count(w, 'runsCompleted'))}</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <Typography level="body-sm" color={count(w, 'runsFailed') > 0 ? 'danger' : undefined}>
                                                        {formatNumber(count(w, 'runsFailed'))}
                                                    </Typography>
                                                </td>
                                                <td style={{ textAlign: 'right' }}>{quietFor(w.totals.lastEventAt)}</td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ textAlign: 'right' }}>—</td>
                                                <td style={{ textAlign: 'right' }}>
                                                    <Budget writer={w} />
                                                </td>
                                                <td colSpan={6} style={{ textAlign: 'right' }}>
                                                    <Typography level="body-xs" textColor="text.tertiary">
                                                        {w.unavailable ? 'could not be read; not zero' : 'no code yet'}
                                                    </Typography>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            ) : null}
        </Box>
    )
}
